// @flow
import React, { useMemo, useEffect } from 'react'
import { Marquee } from '../../Marquee'
import { useSpring, animated, useSpringRef, useChain } from '@react-spring/web'
import { styled } from 'styled-components'
import {
	useMissionState,
	getTopScorers,
	startLocalSoundEffect,
	endLocalSoundEffect,
	useLocalDispatch,
	type MVPStudent,
} from '../../../context'
import {
	EOMS_BACKGROUND,
	EOMS_BORDER,
	EOMS_BORDER_RADIUS,
	EOMS_DARK_HIGHLIGHT,
	PRIMARY_GREEN,
	EOMS_BORDER_WIDTH,
} from './styles'
import type { SharedProps } from './types'
import { cssPlayerHeightUnit } from '../../../utility/constants'
import {
	TOP_STUDENT_DURATION_PER_STUDENT,
	TOP_STUDENT_EXPANDING_DURATION,
} from './durations'
import { SPECIAL_EFFECT_IDS as SOUNDS } from '@mission.io/mission-toolkit/constants'
import { TOP_SCORES_MAX_STUDENTS } from './constants'
import { useCurrentAnalytics } from '../../../context/provider'

/**
 * TopScores - Displays the animated TopScores End Of Mission Sequence component. Sets up the props and
 *    guards against rerenders.
 *
 *
 * @param  {Object} props - the react props
 * @param  {started} props.started - true if the animation should or should have already started
 * @param  {() => void} props.onFinish - a call back for when the animations finishes
 *
 * @returns React$Node
 */
export default function TopScores({
	started,
	onFinish,
}: SharedProps): React$Node {
	const missionState = useMissionState()
	const missionAnalytics = useCurrentAnalytics()

	const topScoreStudents = useMemo(() => {
		return getTopScorers(missionState, missionAnalytics)
	}, [missionState, missionAnalytics])

	return useMemo(
		() => (
			<TopScoresComponent
				started={started}
				onFinish={onFinish}
				topStudents={topScoreStudents}
			/>
		),
		[topScoreStudents, started, onFinish]
	)
}

/**
 * TopScoresComponent - animated top score component for the End Of Mission Sequence. You should guard against rerendering this component
 *    so that the animations work correctly.
 *
 * @param  {Object} props - the react props
 * @param  {started} props.started - true if the animation should or should have already started
 * @param  {() => void} props.onFinish - a call back for when the animations finishes
 * @param  {MVPStudent[]} props.topStudents - the list of students with the top scores
 *
 * @returns React$Node
 */
function TopScoresComponent({
	topStudents,
	started,
	onFinish,
}: {
	topStudents: MVPStudent[],
	...SharedProps,
}): React$Node {
	const dispatch = useLocalDispatch()

	const studentsToDisplay = Math.min(
		TOP_SCORES_MAX_STUDENTS,
		topStudents.length
	)

	useEffect(
		() => () => {
			dispatch(endLocalSoundEffect(SOUNDS.APPLAUSE))
		},
		[dispatch]
	)

	const springRefs = []
	const studentSprings = []
	for (let i = 0; i < TOP_SCORES_MAX_STUDENTS; i++) {
		// the order and mount stays constant on each render
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const springRef = useSpringRef()
		studentSprings.push(
			// the order and mount stays constant on each render
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useSpring({
				ref: springRef,
				config: { duration: TOP_STUDENT_DURATION_PER_STUDENT },
				from: { opacity: 0 },
				to: { opacity: 1 },
				onStart: () => {
					if (i === studentsToDisplay - 1) {
						dispatch(startLocalSoundEffect(SOUNDS.EOMS_SUCCESS_SPARKLE))
					} else {
						dispatch(startLocalSoundEffect(SOUNDS.EOMS_SUCCESS_CLICK))
					}
				},
				onRest: () => {
					if (i === studentsToDisplay - 1) {
						onFinish()
					}
				},
			})
		)
		springRefs.push(springRef)
	}

	const expandingRef = useSpringRef()
	const expandingStyles = useSpring({
		ref: expandingRef,
		config: { duration: TOP_STUDENT_EXPANDING_DURATION },
		from: { height: '0%', borderWidth: 0 },
		to: { height: '100%', borderWidth: EOMS_BORDER_WIDTH },
		onStart: () => {
			dispatch(startLocalSoundEffect(SOUNDS.EOMS_APPEAR))
		},
		onRest: () => {
			if (!topStudents.length) {
				onFinish()
			}
		},
	})

	useChain(
		started ? [expandingRef, ...springRefs.slice(0, studentsToDisplay)] : []
	)

	const students = []

	for (let i = 0; i < studentsToDisplay; i++) {
		students.push(
			<RenderStudent
				key={i}
				student={topStudents[i]}
				springProps={studentSprings[studentsToDisplay - i - 1]}
			/>
		)
	}

	return (
		<Container>
			<HeightWrapper>
				<Header>TOP SCORES</Header>
				<RowSizer>
					<RowResizer
						style={{
							height: expandingStyles.height,
							borderWidth: expandingStyles.borderWidth.interpolate(width =>
								cssPlayerHeightUnit(width)
							),
						}}>
						{topStudents.length ? (
							<Row>{students}</Row>
						) : (
							<NoStudents>
								<div>No Students</div>
							</NoStudents>
						)}
					</RowResizer>
				</RowSizer>
			</HeightWrapper>
		</Container>
	)
}
/**
 * RenderStudent - render a student who has a top score
 *
 * @param  {Object} props - react props
 * @param  {Object} props.student - the student to render
 * @param  {string} props.student.firstName - the fist name of the student
 * @param  {string} props.student.lastName - the last name of the student
 * @param  {number} props.student.score - the number of points the student got
 * @param  {string} props.student.id - the id of the student
 * @param  {Object} props.springProps - the react spring props used to animate this component
 *
 * @returns React$Node
 */
function RenderStudent({
	student: { firstName, lastName, score, id },
	springProps,
}: {
	student: MVPStudent,
	springProps?: {},
}): React$Node {
	return (
		<StudentCell style={springProps}>
			<Marquee autoplay loop leadingTime={1000} trailingTime={1000}>
				{firstName || ''}
			</Marquee>
			<Padder>
				<Marquee autoplay loop leadingTime={1000} trailingTime={1000}>
					{lastName || ''}
				</Marquee>
			</Padder>
			<PointsHighlighter>
				{Math.floor(score).toLocaleString()}
				<small> pts</small>
			</PointsHighlighter>
		</StudentCell>
	)
}

const StudentCell = styled(animated.div)`
	background-color: ${EOMS_DARK_HIGHLIGHT};
	border-radius: 10%;
	text-align: center;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	font-size: ${cssPlayerHeightUnit(2.5)};
	padding: ${cssPlayerHeightUnit(2)} ${cssPlayerHeightUnit(1)};
	max-width: 22%;
	margin: 0 1.5%;
	min-width: 22%;
`

const HeightWrapper = styled.div`
	overflow: hidden;
`

const Row = styled.div`
	max-width: 100%;
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: row;
	align-items: center;
	flex-wrap: wrap;
	justify-content: space-between;
	padding: ${cssPlayerHeightUnit(6)} ${cssPlayerHeightUnit(4.5)};
`

const RowResizer = styled(animated.div)`
	width: 100%;
	overflow: hidden;
	background-color: ${EOMS_BACKGROUND};
	border: ${EOMS_BORDER};
	border-top: none;
	border-radius: 0 0 ${EOMS_BORDER_RADIUS} ${EOMS_BORDER_RADIUS};
`

const RowSizer = styled.div`
	width: 100%;
	height: ${cssPlayerHeightUnit(62)};
`

const Container = styled.div`
	margin-top: ${cssPlayerHeightUnit(5)};
	width: 80%;
`

const Header = styled.div`
	background-color: ${EOMS_BACKGROUND};
	border: ${EOMS_BORDER};
	border-radius: ${EOMS_BORDER_RADIUS} ${EOMS_BORDER_RADIUS} 0 0;
	font-size: ${cssPlayerHeightUnit(3.5)};
	padding: ${cssPlayerHeightUnit(3.5)};
	text-align: center;
`

const NoStudents = styled.div`
	font-size: ${cssPlayerHeightUnit(3)};
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
	height: 100%;
`

const PointsHighlighter = styled.div`
	background-color: ${PRIMARY_GREEN};
	border-radius: ${cssPlayerHeightUnit(1)};
	margin: ${cssPlayerHeightUnit(2)} 5% 0 5%;
	padding: ${cssPlayerHeightUnit(1)};
`

const Padder = styled.div`
	margin-top: ${cssPlayerHeightUnit(0.5)};
`
